import { Plus, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { isValidSlug } from './adminValidation';
import type { FormEditorProps } from '../../types/admin';

export function FormEditor({ form, onChange, onSave, onCancel, createField }: FormEditorProps) {
  const updateField = (field: keyof typeof form, value: any) => {
    onChange({ ...form, [field]: value });
  };

  const updateFields = (fields: NonNullable<typeof form.fields>) => {
    onChange({ ...form, fields });
  };

  const addField = () => {
    updateFields([...(form.fields || []), createField()]);
  };

  const updateFormField = (index: number, field: string, value: any) => {
    const next = [...(form.fields || [])];
    next[index] = { ...next[index], [field]: value };
    updateFields(next);
  };

  const removeField = (index: number) => {
    updateFields((form.fields || []).filter((_, idx) => idx !== index));
  };

  return (
    <div className="glass rounded-2xl p-6 mb-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          value={form.slug || ''}
          onChange={(e) => updateField('slug', e.target.value)}
          className="bg-zinc-900/50 border-white/10 text-white"
          placeholder="Slug"
        />
        <Input
          value={form.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          className="bg-zinc-900/50 border-white/10 text-white"
          placeholder="Title (RU)"
        />
        <Input
          value={form.title_en || ''}
          onChange={(e) => updateField('title_en', e.target.value)}
          className="bg-zinc-900/50 border-white/10 text-white"
          placeholder="Title (EN)"
        />
        <select
          value={form.publication_status || 'draft'}
          onChange={(e) => updateField('publication_status', e.target.value)}
          className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
      {form.slug && !isValidSlug(form.slug) && (
        <p className="text-red-300 text-xs">Use lowercase kebab-case only.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          value={form.submit_message || ''}
          onChange={(e) => updateField('submit_message', e.target.value)}
          className="bg-zinc-900/50 border-white/10 text-white"
          placeholder="Submit message (RU)"
        />
        <Input
          value={form.submit_message_en || ''}
          onChange={(e) => updateField('submit_message_en', e.target.value)}
          className="bg-zinc-900/50 border-white/10 text-white"
          placeholder="Submit message (EN)"
        />
      </div>

      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold">Fields</h4>
        <Button onClick={addField} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" />
          Add field
        </Button>
      </div>

      <div className="space-y-4">
        {(form.fields || []).map((field, index) => (
          <div key={field.id || index} className="glass rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                value={field.label || ''}
                onChange={(e) => updateFormField(index, 'label', e.target.value)}
                className="bg-zinc-900/50 border-white/10 text-white"
                placeholder="Label (RU)"
              />
              <Input
                value={field.label_en || ''}
                onChange={(e) => updateFormField(index, 'label_en', e.target.value)}
                className="bg-zinc-900/50 border-white/10 text-white"
                placeholder="Label (EN)"
              />
              <select
                value={field.type || 'text'}
                onChange={(e) => updateFormField(index, 'type', e.target.value)}
                className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2"
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="textarea">Textarea</option>
                <option value="select">Select</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-white/70 text-sm">
                <input
                  type="checkbox"
                  checked={field.required || false}
                  onChange={(e) => updateFormField(index, 'required', e.target.checked)}
                />
                Required
              </label>
              {field.type === 'select' && (
                <div className="flex flex-col md:flex-row gap-2 w-full">
                  <Input
                    value={(field.options || []).join(', ')}
                    onChange={(e) => updateFormField(index, 'options', e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
                    className="bg-zinc-900/50 border-white/10 text-white"
                    placeholder="Options RU (comma separated)"
                  />
                  <Input
                    value={(field.options_en || []).join(', ')}
                    onChange={(e) => updateFormField(index, 'options_en', e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
                    className="bg-zinc-900/50 border-white/10 text-white"
                    placeholder="Options EN (comma separated)"
                  />
                </div>
              )}
              <Button onClick={() => removeField(index)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-4 pt-4">
        <Button onClick={onCancel} className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-xl">
          Cancel
        </Button>
        <Button onClick={onSave} className="flex-1 bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-xl">
          <Save className="w-5 h-5 mr-2" />
          Save Form
        </Button>
      </div>
    </div>
  );
}
